import CircularProgress from '@mui/material/CircularProgress';
import { useEffect, useState, useRef, ChangeEvent, useReducer } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import InfoCard from 'components/InfoCard/InfoCard';
import useAxiosGet from 'functions/hooks/useAxiosGet';
import useGetUsers from 'functions/hooks/useGetUsers';
import useGetVehicles from 'functions/hooks/useGetVehicles';
import { FormLayout } from 'layouts/FormLayout/FormLayout';
import { SubscriptionPlan } from 'types/SubscriptionPlan';
import Button from 'components/Button/Button';
import { useAppSettings } from 'context/appsettingsContext';
import axios from 'axios';
import { useAuth } from 'context/useAuth';
import Modal from 'components/Modal/Modal';
import CustomOption from 'components/CustomOption/CustomOption';
import { useCollection } from 'functions/hooks/useCollection';
import './ViewAccount.scss';
import { confirmAsync, errorAsync, promptAsync, successAsync } from 'functions/notification';
import { useRefreshComponent } from 'functions/deviceStatusUtils';
import { Timestamp } from 'firebase/firestore';

export interface Device {
  id: string;
  active: boolean;
  serialNumber: string;
  testStation?: string;
}

export default function ViewAccount() {

  const navigate = useNavigate();
  const location = useLocation();
  const settings = useAppSettings();
  const refreshComponent = useRefreshComponent();

  const { clientId } = useParams();
  const [ linking, setLinking ] = useState(false);
  const [ unlinking, setUnlinking ] = useState(false);
  const [ sendingNotification, setSendingNotification ] = useState(false);
  const [ deletingAccount, setDeletingAccount ] = useState(false);
  const { data: subscriptionPlans } = useAxiosGet('SUBSCRIPTION_PLANS');
  const { users: user, loading } = useGetUsers('ACCOUNT_HOLDERS_BY_CLIENT_ID', { clientId });
  const { authorisedUser } = useAuth();
  const { vehicles, loadingVehicles } = useGetVehicles('VEHICLES_BY_CLIENT_ID', { clientId });
  const deviceModalRef = useRef(null);
  const [selectedVehicle, setSelectedVehicle] = useState<{ id: string, deviceId: string } | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [allDevices, setAllDevices] = useState<Device[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<Device>();
  const [searchTextLowerCase, setSearchTextLowerCase] = useState('');
  const devicesCollection = useCollection<any>('devices');
  const vehiclesCollection = useCollection<any>('vehicles');
  const vehicleDeviceChangesCollection = useCollection<any>('vehicleDeviceChanges');

  // Devices for the link-device modal are loaded lazily — only when the user
  // opens the modal AND types ≥ 3 chars (handleDeviceSearch). Previously we
  // eagerly pulled the entire devices + vehicles collections on mount which
  // dominated the View Account open time.

  async function getDevices(searchText: string) {
    try {
      // Get all vehicles
      let vehicles = (await vehiclesCollection.queryMany()).map(x => ({
        id: x.id,
        deviceId: x.data.deviceId,
      }));

      let newDevices = (await devicesCollection.queryMany()).map(x => ({
        id: x.id,
        active: true,
        serialNumber: x.id,
      })).sort((a, b) => a.serialNumber.localeCompare(b.serialNumber));

      // Filter out devices that are already linked to a vehicle
      newDevices = newDevices.filter(device => !vehicles.some(vehicle => vehicle.deviceId === device.id));

      if (searchText) {
        newDevices = newDevices.filter(x => x.id.includes(searchText));
      }

      const linkedDeviceIds = new Set(vehicles.map(vehicle => vehicle.deviceId));
      const filteredDevices = newDevices.filter(device => !linkedDeviceIds.has(device.id));

      setAllDevices(filteredDevices);
    } catch (error: any) {
      console.log(error);
    }
  }

  const handleGoBack = () => {
    if (location.state?.from?.pathname.includes("add-driver")) {
      navigate("/agent/onboarding/outstanding-details");
    } else {
      navigate("/agent/clients");
    }
  };

  const handleResendPasswordLink = async () => {
    try {
      
      setSendingNotification(true);

      const response = await axios.post(settings.sendPasswordResetNotificationUrl,{
        userId : clientId
      },{ headers: { Authorization: `Bearer ${authorisedUser.accessToken}` } });

      successAsync(response?.data?.message || "Notification Sent");

    } catch (error: any) {
      errorAsync(`Failed to send notification : ${error?.response?.data?.message || error?.message || error}`, error);
    } finally {
      setSendingNotification(false);
    }
  };

  const handleLinkDeviceClick = (vehicle: any) => {
    setSelectedDevice(undefined);
    setSelectedVehicle(vehicle);
  };

  const handleDeviceUnlinkClick = async (vehicle: any) => {
    if (await confirmAsync(`Device will be unlinked from vehicle. Are you sure you want to proceed?`, { title: 'Unlink Device', icon: 'warning', confirmButtonText: 'Unlink' })) {
      try {

        setUnlinking(true);

        await vehiclesCollection.insertOrUpdate(vehicle.id, { deviceId: null });
        await vehicleDeviceChangesCollection.insertOne({ type: 'unlink', vehicleId: vehicle.id, oldDeviceId: vehicle.deviceId, timestamp: Timestamp.now(), byUserId: authorisedUser.userId, byUserFullName: authorisedUser.userFullName });

        Object.assign(vehicle, { deviceId: null });

        refreshComponent();
        
        (deviceModalRef?.current as any)?.close();

        await getDevices("");
        
      } catch (error: any) {
        errorAsync(`Failed to unlink : ${error?.response?.data?.message || error?.message || error}`, error);
      } finally {
        setUnlinking(false);
      }
    }
  };

  const abortDeviceSearch = () => {
    setSelectedDevice(undefined);
  };

  const handleDeviceSelect = (device: Device) => {
    setSelectedDevice(device);
  };

  const handleDeviceSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSelectedDevice(undefined);
    if (value.length >= 3) {
      getDevices(value);
    }
  };

  const linkDeviceToVehicle = async () => {
    if (selectedVehicle && selectedDevice) {
      try {

        setLinking(true);

        await vehiclesCollection.insertOrUpdate(selectedVehicle.id, { deviceId: selectedDevice.id });
        await vehicleDeviceChangesCollection.insertOne({ type: 'link', vehicleId: selectedVehicle.id, oldDeviceId: selectedVehicle.deviceId, newDeviceId: selectedDevice.id, timestamp: Timestamp.now(), byUserId: authorisedUser.userId, byUserFullName: authorisedUser.userFullName });
        
        Object.assign(selectedVehicle, { deviceId: selectedDevice.id });
  
        successAsync('Linked');

        refreshComponent();
        
        (deviceModalRef?.current as any)?.close();

        await getDevices("");
        
      } catch (error: any) {
        errorAsync(`Failed to link : ${error?.response?.data?.message || error?.message || error}`, error);
      } finally {
        setLinking(false);
      }
    }
  };

  const deleteAccount = async () => {
    const fullName = `${user[0]?.firstName} ${user[0]?.lastName}`;
    if (await promptAsync(`This cannot be reversed.<br>Type '${fullName}' below<br>and press Delete to confirm.`, { title: 'Delete Account', icon: 'error', confirmButtonText: 'Delete' }) === fullName) {
      try {

        setDeletingAccount(true);

        await axios.post<any, any>(settings.deleteUserUrl, {
          userId: clientId
        }, { headers: { Authorization: `Bearer ${authorisedUser.accessToken}` } });
  
        successAsync("Deleted");
        navigate(`/agent/clients/all`);
  
      } catch (error: any) {
        errorAsync(`Failed to delete : ${error?.response?.data?.message || error?.message || error}`, error);
      } finally {
        setSendingNotification(false);
      }
    }
  };

  const renderAddDeviceModalContent = () => {
    return (
      <>
        {
          !selectedDevice && (
            <>
              <div className='form-field'>
                <small>Type at least 3 characters for matches to be listed below</small>
                <input
                  id='Device'
                  autoComplete='off'
                  placeholder='Type to select'
                  onChange={handleDeviceSearch}
                  className='vehicle-card__deviceSerialNumberDropdown'
                />
              </div>

              <div className='results' style={{ position: 'static', height: '13.5rem', border: '1px solid rgb(194, 198, 199)' }}>
                {allDevices.map(
                  (device: Device) => (
                    <CustomOption
                      key={device.id}
                      className='option'
                      id={device.id.toString()}
                      value={device.serialNumber}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleDeviceSelect(device)}
                    >
                      {device.serialNumber}
                    </CustomOption>
                  )
                )}
              </div>
            </>
          )
        }

        {/* Render selected device */}
        {selectedDevice && (
          <div className='form-field'>
            <p>
              <strong>Selected Device:</strong> {selectedDevice.serialNumber}
            </p>
          </div>
        )}

        <div style={{ flexDirection: 'row', textAlign: 'center', marginTop: 20 }}>
          <Button
            label='Clear'
            onClick={() => setSelectedDevice(undefined)}
            style={{ marginLeft: 5, marginRight: 5 }}
            disabled={!selectedDevice}
          />
          <Button
            label='OK'
            onClick={linkDeviceToVehicle}
            style={{ marginTop: 10, marginLeft: 5, marginRight: 5 }}
            disabled={!selectedDevice}
          />
        </div>
      </>
    );
  };

  useEffect(() => {
    document.title = 'View Account | Titan Secure';
  });

  return (
    <FormLayout>
      
      <FormLayout.Header>
        <FormLayout.HeaderBack onClose={handleGoBack} />
        <p>View account</p>
      </FormLayout.Header>

      <FormLayout.Main>
        {loading && (
          <div className='account__loader'>
            <CircularProgress />
          </div>
        )}
        {!loading && user.length !== 0 && (
          <>
          <InfoCard
            key={user[0]?.id}
            title={`${user[0]?.firstName} ${user[0]?.lastName}`}
            clientDetails={user[0]}
            onDelete={deleteAccount}
            userUpdated={details => { Object.assign(user[0], details); refreshComponent(); }}
          />
          <div style = {{
            justifyContent: 'center',
            display: 'flex',
          }}>
            <Button
              disabled={sendingNotification}
              onClick={handleResendPasswordLink}
              label = { sendingNotification ? "Sending Password Reset Notification" : "Send Password Reset Notification"}
            />
          </div>
        </>
        )}

        {loadingVehicles && (
          <div className='account__loader'>
            <CircularProgress />
          </div>
        )}

        {!loadingVehicles &&
          vehicles.map((vehicle) => (
            <div key={vehicle?.id}>
              {/**vehicle.drivers.map((driver) => (
                <InfoCard
                  key={driver?.id}
                  title={driver?.firstName + ' ' + driver?.lastName}
                  driverDetails={driver}
                />
              ))**/}

              {/**subscriptionPlans.map((plan: SubscriptionPlan) =>
                plan.id === vehicle.subscriptionPlanId ? (
                  <InfoCard
                    key={plan?.id}
                    subtitle={plan.displayName}
                    field={plan.description}
                  />
                ) : null
              )**/}

              <InfoCard
                key={vehicle?.id}
                title={vehicle?.registrationNumber}
                clientId={clientId}
                vehicleDetails={vehicle}
                vehicleUpdated={details => { Object.assign(vehicle, details); refreshComponent(); }}
              />

              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                <Modal
                  ref={deviceModalRef}
                  onClose={abortDeviceSearch}
                  title={vehicle.deviceId ? "Change Device" : "Link Device"}
                  trigger={
                    <Button
                      style={{ marginRight: 10 }}
                      onClick={() => handleLinkDeviceClick(vehicle)}
                      label={vehicle.deviceId ? "Change Device" : "Link Device"}
                    />
                  }
                >
                  <div className='form-field-search'>
                    {renderAddDeviceModalContent()}
                  </div>
                </Modal>
                {vehicle.deviceId && (
                  <Button
                    onClick={() => handleDeviceUnlinkClick(vehicle)}
                    label={"Unlink Device"}
                    />
                )}
              </div>
            </div>
          ))}
      </FormLayout.Main>

    </FormLayout>
  );
}